import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from 'context/AuthContext';
import axiosInstance, { refreshSession, clearSessionState } from 'utils/axiosConfig';
import { FAST_API_BASE_URL } from 'constants';

const MINUTES = (m) => m * 60 * 1000;

const IDLE_TIMEOUT_MINUTES    = 15;
const WARNING_SHOWN_AT_MINUTE  = 13;

const AUTO_LOGOUT_AFTER_MS = MINUTES(IDLE_TIMEOUT_MINUTES);
const WARNING_WINDOW_MS    = MINUTES(IDLE_TIMEOUT_MINUTES - WARNING_SHOWN_AT_MINUTE); // 2 min
const TICK_MS              = 1000;

const USER_ACTIVITY_EVENTS = [
  'mousedown', 'mousemove', 'keydown', 'scroll',
  'touchstart', 'click', 'input', 'focus', 'wheel',
];

const isRememberMeEnabled = () => localStorage.getItem('rememberMe') === 'true';


export const useSessionManager = () => {
  const { isAuthenticated, logout } = useAuth();

  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(0);

  // Absolute timestamp (ms) at which the session must be logged out. Everything
  // is derived from this so the countdown stays correct even if the browser
  // throttles / pauses timers in a background tab.
  const deadlineRef    = useRef(0);
  const tickRef        = useRef(null);
  const loggingOutRef  = useRef(false);
  const warningShownRef = useRef(false);

  const clearTick = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const hideWarning = useCallback(() => {
    warningShownRef.current = false;
    setIsWarningVisible(false);
    setCountdownSeconds(0);
  }, []);

  const logoutDueToInactivity = useCallback(async () => {
    if (loggingOutRef.current) return;   // guard against double-fire
    loggingOutRef.current = true;

    clearTick();
    hideWarning();

    try {
      await axiosInstance.post(
        `${FAST_API_BASE_URL}/api/auth/logout`,
        {},
        { withCredentials: true }
      );
    } catch {
      // ignore – we're logging out regardless
    }

    clearSessionState();
    logout();
    window.location.href = '/login';
  }, [clearTick, hideWarning, logout]);

  // Single self-correcting ticker. Reads the absolute deadline every second, so
  // if the tab was suspended it catches up instantly instead of freezing.
  const startTicker = useCallback(() => {
    clearTick();
    tickRef.current = setInterval(() => {
      const remaining = deadlineRef.current - Date.now();

      if (remaining <= 0) {
        logoutDueToInactivity();
        return;
      }

      if (remaining <= WARNING_WINDOW_MS) {
        warningShownRef.current = true;
        setIsWarningVisible(true);
        setCountdownSeconds(Math.ceil(remaining / 1000));
      }
    }, TICK_MS);
  }, [clearTick, logoutDueToInactivity]);

  const armDeadline = useCallback(() => {
    deadlineRef.current = Date.now() + AUTO_LOGOUT_AFTER_MS;
    startTicker();
  }, [startTicker]);

  const restartInactivityTimer = useCallback(() => {
    if (!isAuthenticated())        return;
    if (isRememberMeEnabled())     return;   // remembered sessions never idle-out here
    if (warningShownRef.current)   return;   // don't let activity reset a live warning
    armDeadline();
  }, [isAuthenticated, armDeadline]);

  const extendSession = useCallback(() => {
    hideWarning();
    refreshSession().catch(console.error);
    armDeadline();
  }, [hideWarning, armDeadline]);


  useEffect(() => {
    // Fully inert for unauthenticated OR "remember me" sessions: no timers,
    // no listeners, and make sure any stale dialog is cleared.
    if (!isAuthenticated() || isRememberMeEnabled()) {
      clearTick();
      hideWarning();
      return;
    }

    USER_ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, restartInactivityTimer, { passive: true })
    );

    // If the user is logged out through ANY other path (server session expiry
    // -> sessionExpired event, axios refresh failure, etc.) tear the dialog down
    // so it can't get stuck on top of the /login screen.
    const handleExternalLogout = () => {
      clearTick();
      hideWarning();
    };
    window.addEventListener('sessionExpired', handleExternalLogout);

    restartInactivityTimer();

    return () => {
      USER_ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, restartInactivityTimer)
      );
      window.removeEventListener('sessionExpired', handleExternalLogout);
      clearTick();
    };
  }, [isAuthenticated, restartInactivityTimer, clearTick, hideWarning]);


  return {
    isWarningVisible,
    countdownSeconds,
    extendSession,
    hideWarning,
  };
};
